/**
 * Exportable Proposal View
 *
 * This component renders the proposal using the EXACT SAME SectionRenderers
 * that appear in the proposal viewer modal. "What you see is what you get."
 *
 * It's designed to be captured by react-to-pdf for pixel-perfect PDF exports.
 */

import React from 'react';
import type { Contract } from '../../types';
import type { DeliverablesData } from '../../types/deliverables';
import {
  CoverLetterRenderer,
  ExecutiveSummaryRenderer,
  TechnicalApproachRenderer,
  ManagementApproachRenderer,
  PastPerformanceRenderer,
  PersonnelRenderer,
  PricingRenderer,
  ComplianceRenderer,
} from './SectionRenderers';
import { ProposalSectionRenderer } from './ProposalSectionRenderer';
import { DeliverablesSectionContent } from './DeliverablesSectionContent';
import { ProductSynthesis } from './ProductSynthesis';
import { ExportCoverPage } from './ExportCoverPage';
import { buildCoverPageModel } from '../../lib/export/coverPageModel';
import { buildCompanyIdentity, type ExportCompanyIdentity } from '../../lib/export/companyIdentity';
import {
  isCoverLetterContent,
  isExecutiveSummaryContent,
  isTechnicalApproachContent,
  isManagementApproachContent,
  isPastPerformanceContent,
  isPersonnelContent,
  isPricingContent,
  isComplianceContent,
} from '../../types/proposal-sections';

// ============================================================================
// TYPES
// ============================================================================

interface ProposalContent {
  coverLetter: string;
  technicalSections: Array<{
    title: string;
    content: string;
  }>;
  feedback?: string;
  contractAnalysis?: string;
  profileAnalysis?: string;
  message?: string;
  aiFeedback?: any;
  deliverablesData?: DeliverablesData;
}

interface ExportableProposalViewProps {
  proposal: ProposalContent;
  contract: Contract;
  selectedSections: string[];
  showCitations?: boolean;
  separateCoverLetter?: boolean;
  identity?: ExportCompanyIdentity;
}

// ============================================================================
// HELPER: Parse JSON content
// ============================================================================

function parseContent(content: string): any {
  if (!content) return null;
  if (typeof content === 'object') return content;
  try {
    return JSON.parse(content);
  } catch {
    return null;
  }
}

// ============================================================================
// FALLBACK TEXT RENDERER
// ============================================================================

function FallbackTextRenderer({ content, showCitations }: { content: string; showCitations: boolean }) {
  if (!content) return null;

  // Strip citations if needed
  const processedContent = showCitations
    ? content
    : content.replace(/\[[A-Z]+-\d+(?:,\s*[^\]]+)?\]/g, '');

  // Split into paragraphs and render
  const paragraphs = processedContent.split('\n\n').filter(p => p.trim());

  return (
    <div className="prose prose-slate max-w-none">
      {paragraphs.map((para, idx) => {
        const trimmed = para.trim();

        // Check if it's a header (ALL CAPS or starts with ##)
        if (trimmed === trimmed.toUpperCase() && trimmed.length < 60 && trimmed.length > 2) {
          return (
            <h3 key={idx} className="text-lg font-bold text-bw-ink mt-6 mb-3">
              {trimmed}
            </h3>
          );
        }

        // Bullet points
        if (trimmed.startsWith('•') || trimmed.startsWith('-') || trimmed.startsWith('*')) {
          const items = trimmed.split('\n').filter(l => l.trim());
          return (
            <ul key={idx} className="list-disc list-inside text-bw-ink space-y-1 mb-4">
              {items.map((item, iidx) => (
                <li key={iidx}>{item.replace(/^[•\-*]\s*/, '')}</li>
              ))}
            </ul>
          );
        }

        // Regular paragraph
        return (
          <p key={idx} className="text-bw-ink leading-relaxed mb-4">
            {trimmed}
          </p>
        );
      })}
    </div>
  );
}

// ============================================================================
// SECTION WRAPPER WITH PAGE BREAK
// ============================================================================

function SectionWrapper({
  title,
  children,
  isFirst = false,
  landscape = false,
}: {
  title: string;
  children: React.ReactNode;
  isFirst?: boolean;
  landscape?: boolean;
}) {
  return (
    <section
      className={`mb-8 ${isFirst ? '' : 'page-break-before'} ${landscape ? 'export-landscape-section' : ''}`}
      style={{ pageBreakBefore: isFirst ? 'auto' : 'always' }}
    >
      <h1 className="text-2xl font-bold text-bw-primary mb-6 pb-3 border-b-2 border-blue-600">
        {title}
      </h1>
      {children}
    </section>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function ExportableProposalView({
  proposal,
  contract,
  selectedSections,
  showCitations = false,
  separateCoverLetter = false,
  identity,
}: ExportableProposalViewProps) {
  const parsedCoverLetter = parseContent(proposal.coverLetter);
  const resolvedIdentity = identity ?? buildCompanyIdentity(null, parsedCoverLetter);
  const coverModel = buildCoverPageModel(contract, resolvedIdentity, { coverLetter: parsedCoverLetter });
  const hasCoverLetter = selectedSections.includes('coverLetter') && !separateCoverLetter && proposal.coverLetter;
  const hasDeliverables = selectedSections.includes('deliverables') && (proposal.deliverablesData?.deliverables_with_labor?.length ?? 0) > 0;
  const hasProduct = selectedSections.includes('product') && proposal.deliverablesData?.product_definition;

  // Track section index for page breaks
  let sectionIndex = 0;

  const renderSection = (section: { title: string; content: string }) => {
    const sectionId = section.title.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');

    if (!selectedSections.includes(sectionId)) {
      return null;
    }

    const isFirst = sectionIndex === 0 && !hasCoverLetter;
    sectionIndex++;
    // The Pricing tables (CLIN schedule, labor-rate buildup) run 7-10 columns
    // wide — too wide for a 6.5in portrait page. Give that section its own
    // landscape page(s) so the tables aren't crushed.
    const isPricing = section.title.toLowerCase().includes('pricing');

    // Delegate to the SAME renderer used on screen so the export is a true
    // "what you see is what you get" copy (structured renderers + markdown
    // fallback, identical to the proposal viewer).
    return (
      <SectionWrapper key={sectionId} title={section.title} isFirst={isFirst} landscape={isPricing}>
        <ProposalSectionRenderer
          sectionType={sectionId}
          content={section.content}
          showCitations={showCitations}
          pricingView="federal"
          pricingHideToggle
        />
      </SectionWrapper>
    );
  };

  // Render Cover Letter
  const renderCoverLetter = () => {
    if (!hasCoverLetter) return null;

    const isFirst = sectionIndex === 0;
    sectionIndex++;

    return (
      <SectionWrapper title="Cover Letter" isFirst={isFirst}>
        <ProposalSectionRenderer
          sectionType="coverLetter"
          content={proposal.coverLetter}
          showCitations={showCitations}
        />
      </SectionWrapper>
    );
  };

  // Render Deliverables Section
  const renderDeliverables = () => {
    if (!hasDeliverables) return null;

    const isFirst = sectionIndex === 0;
    sectionIndex++;

    return (
      <SectionWrapper title="Deliverables Analysis" isFirst={isFirst}>
        <DeliverablesSectionContent
          deliverables={proposal.deliverablesData}
          className="export-deliverables"
        />
      </SectionWrapper>
    );
  };

  // Render Product Section
  const renderProduct = () => {
    if (!hasProduct) return null;

    const isFirst = sectionIndex === 0;
    sectionIndex++;

    return (
      <SectionWrapper title="Product/Solution Definition" isFirst={isFirst}>
        <ProductSynthesis
          productDefinition={proposal.deliverablesData?.product_definition}
          className="export-product"
        />
      </SectionWrapper>
    );
  };

  return (
    <div className="export-print-theme export-container print-colors">
      {/* Cover Page (page 1, no running header/footer) */}
      <ExportCoverPage model={coverModel} />

      {/* Cover Letter */}
      {renderCoverLetter()}

      {/* Technical Sections */}
      {proposal.technicalSections.map(section => renderSection(section))}

      {/* Deliverables Section */}
      {renderDeliverables()}

      {/* Product Section */}
      {renderProduct()}

      {/* Light per-section spacing; the authoritative Formal-Federal theme and
          page furniture are injected at print time (see lib/export/exportStyles). */}
      <style>{`
        .export-print-theme .export-deliverables .space-y-6 > div,
        .export-print-theme .export-product .space-y-6 > div { margin-bottom: 1.5rem; }
      `}</style>
    </div>
  );
}

export default ExportableProposalView;
